import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowRight, Building2, Newspaper, Users, Briefcase } from "lucide-react";
import { projectData } from "../data/projectsData";
import { getNewsArticles } from "../data/newsData";
import { partners } from "../data/collaborationsData";
import { getServices } from "../data/servicesData";
import { useLanguage } from "../contexts/LanguageContext";

export default function SearchOverlay({ isOpen, onClose, onNavigate }) {
    const { t, language } = useLanguage();
    const newsArticles = getNewsArticles(language);
    const [searchTerm, setSearchTerm] = useState("");
    const [results, setResults] = useState({
        projects: [],
        news: [],
        partners: [],
        services: []
    });
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current.focus(), 100);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setResults({ projects: [], news: [], partners: [], services: [] });
            return;
        }

        const term = searchTerm.toLowerCase();

        // Filter Projects
        const filteredProjects = Object.entries(projectData).filter(([key, project]) => {
            const title = (t.projects[project.titleKey] || '').toLowerCase();
            const subtitle = (t.projects[project.subtitleKey] || '').toLowerCase();
            return title.includes(term) || subtitle.includes(term);
        }).map(([key, project]) => ({ ...project, id: key }));

        // Filter News
        const filteredNews = newsArticles.filter(article =>
            article.title.toLowerCase().includes(term) ||
            article.description.toLowerCase().includes(term)
        );

        // Filter Partners
        const filteredPartners = partners.filter(partner => {
            const desc = (t.collaborations[partner.descriptionKey] || '').toLowerCase();
            return partner.name.toLowerCase().includes(term) || desc.includes(term);
        });

        // Filter Services
        const services = getServices(t);
        const filteredServices = services.filter(service =>
            service.label.toLowerCase().includes(term)
        );

        setResults({
            projects: filteredProjects,
            news: filteredNews,
            partners: filteredPartners,
            services: filteredServices
        });
    }, [searchTerm, t, language, newsArticles]);

    const handleNavigate = (sectionId) => {
        onNavigate(sectionId);
        onClose();
    };

    const hasResults = Object.values(results).some(arr => arr.length > 0);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex flex-col"
                >
                    {/* Header / Search Input */}
                    <div className="container mx-auto px-6 py-8">
                        <div className="flex items-center justify-between mb-8">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="flex-1 mr-4"
                            >
                                <div className="relative group">
                                    <Search className="absolute left-0 top-1/2 transform -translate-y-1/2 w-6 h-6 text-[--text-secondary] group-focus-within:text-[--accent] transition-colors" />
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder={language === 'en' ? 'Search...' : 'Cerca...'}
                                        className="w-full bg-transparent border-b-2 border-gray-700 focus:border-[--accent] text-2xl md:text-4xl py-4 pl-10 pr-4 text-white placeholder-gray-600 focus:outline-none transition-colors font-display"
                                    />
                                </div>
                            </motion.div>
                            <button
                                onClick={onClose}
                                className="w-12 h-12 rounded-full bg-[--bg-secondary] hover:bg-[--accent] flex items-center justify-center transition-colors group"
                            >
                                <X className="w-6 h-6 text-white group-hover:text-black" />
                            </button>
                        </div>
                    </div>

                    {/* Results Area */}
                    <div className="flex-1 overflow-y-auto px-6 pb-20">
                        <div className="container mx-auto">
                            {!searchTerm && (
                                <div className="flex flex-col items-center justify-center h-64 text-[--text-secondary]">
                                    <Search className="w-16 h-16 mb-4 opacity-20" />
                                    <p className="text-xl">{language === 'en' ? 'Start typing to search' : 'Inizia a digitare per cercare'}</p>
                                </div>
                            )}

                            {searchTerm && !hasResults && (
                                <div className="text-center py-20 text-[--text-secondary]">
                                    <p className="text-xl">{language === 'en' ? `No results found for "${searchTerm}"` : `Nessun risultato trovato per "${searchTerm}"`}</p>
                                </div>
                            )}

                            {/* Projects Results */}
                            {results.projects.length > 0 && (
                                <section className="mb-12">
                                    <div className="flex items-center mb-6">
                                        <Building2 className="w-5 h-5 text-[--accent] mr-3" />
                                        <h3 className="text-xl font-bold text-white uppercase tracking-wider">{t.projects.title}</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {results.projects.map((project) => (
                                            <div
                                                key={project.id}
                                                onClick={() => handleNavigate('projects')}
                                                className="group cursor-pointer bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors border border-transparent hover:border-[--accent]/30"
                                            >
                                                <div className="flex items-center space-x-4">
                                                    <img src={project.coverImage} alt={t.projects[project.titleKey]} className="w-16 h-16 rounded-lg object-cover" />
                                                    <div>
                                                        <h4 className="text-lg font-bold text-white group-hover:text-[--accent] transition-colors">{t.projects[project.titleKey]}</h4>
                                                        <p className="text-sm text-gray-400">{t.projects[project.subtitleKey]}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* News Results */}
                            {results.news.length > 0 && (
                                <section className="mb-12">
                                    <div className="flex items-center mb-6">
                                        <Newspaper className="w-5 h-5 text-[--accent] mr-3" />
                                        <h3 className="text-xl font-bold text-white uppercase tracking-wider">News</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {results.news.map((article) => (
                                            <div
                                                key={article.id}
                                                onClick={() => handleNavigate('news')}
                                                className="group cursor-pointer bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors border border-transparent hover:border-[--accent]/30"
                                            >
                                                <h4 className="text-lg font-bold text-white group-hover:text-[--accent] transition-colors mb-2">{article.title}</h4>
                                                <p className="text-sm text-gray-400 line-clamp-2">{article.description}</p>
                                                <div className="mt-2 text-xs text-[--accent]">{article.date}</div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Partners Results */}
                            {results.partners.length > 0 && (
                                <section className="mb-12">
                                    <div className="flex items-center mb-6">
                                        <Users className="w-5 h-5 text-[--accent] mr-3" />
                                        <h3 className="text-xl font-bold text-white uppercase tracking-wider">Partners</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {results.partners.map((partner, index) => (
                                            <div
                                                key={index}
                                                onClick={() => handleNavigate('collaborations')}
                                                className="group cursor-pointer bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors border border-transparent hover:border-[--accent]/30 flex items-center space-x-4"
                                            >
                                                <div className="w-12 h-12 bg-white rounded-lg p-2 flex items-center justify-center">
                                                    <img src={partner.image} alt={partner.name} className="max-w-full max-h-full" />
                                                </div>
                                                <div>
                                                    <h4 className="text-base font-bold text-white group-hover:text-[--accent] transition-colors">{partner.name}</h4>
                                                    <p className="text-xs text-gray-400 line-clamp-1">{t.collaborations[partner.descriptionKey]}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Services Results */}
                            {results.services.length > 0 && (
                                <section className="mb-12">
                                    <div className="flex items-center mb-6">
                                        <Briefcase className="w-5 h-5 text-[--accent] mr-3" />
                                        <h3 className="text-xl font-bold text-white uppercase tracking-wider">{t.services.title}</h3>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                        {results.services.map((service, index) => (
                                            <div
                                                key={index}
                                                onClick={() => handleNavigate('home')}
                                                className="group cursor-pointer bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors border border-transparent hover:border-[--accent]/30 flex items-center justify-between"
                                            >
                                                <span className="text-white group-hover:text-[--accent] transition-colors font-medium">{service.label}</span>
                                                <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-[--accent] transition-colors" />
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
